import { Loader, type PlanInterface } from "@beholos/holoscommon";
import { memo, useEffect, useState } from "react";
import { getPaymentMethods, type PaymentMethod, } from "../../services/payment.service";
import CardFields, { type CardFieldsHandle } from "./CardFields";
import type { PromoCode } from "./PaymentModal";
import { SMART_FIT_TERMS_URL, type SmartFitDuration } from "./smartFit.constants";
import { PaymentSummaryCard } from "./PaymentSummaryCard";

if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_STRIPE_PUBLISHABLE_KEY');
}

export const PaymentModalStep2 = memo(({
  selectedPlan,
  basePlan,
  confirmedTerms,
  setConfirmedTerms,
  saveNewCard: _saveNewCard,
  setSaveNewCard: _setSaveNewCard,
  selectedPaymentOption,
  selectedMethod,
  setSelectedMethod,
  error,
  appliedPromo,
  isSmartFitSelected = false,
  smartFitDuration = '1 mes' as SmartFitDuration,
  smartFitPrice = 0,
  onBrickReady,
  onBrickSubmit,
  cardFieldsRef,
}: {
  selectedPlan: PlanInterface,
  basePlan?: PlanInterface,
  confirmedTerms: {
    terms: boolean,
    price: boolean,
    cancel: boolean,
    autoRenew: boolean,
    smartFitTerms: boolean,
  }
  setConfirmedTerms: (terms: any) => void,
  saveNewCard: boolean,
  setSaveNewCard: (save: boolean) => void,
  selectedPaymentOption: 'today' | 'nextCycle',
  selectedMethod: string,
  setSelectedMethod: (method: string) => void,
  error: string | null;
  appliedPromo: PromoCode | null;
  isSmartFitSelected?: boolean;
  smartFitDuration?: SmartFitDuration;
  smartFitPrice?: number;
  onBrickReady?: () => void;
  onBrickSubmit?: (formData: any) => Promise<void>;
  cardFieldsRef?: React.RefObject<CardFieldsHandle | null>;
}) => {
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  useEffect(() => {
    getPaymentMethods().then(methods => {
      const paymentMethodList = methods || [];
      setPaymentMethods(paymentMethodList);
      setSelectedMethod(paymentMethodList[0]?.id || 'new');
    }).finally(() => setLoading(false));
  }, []);

  const holosMonthlyCharge = selectedPlan.price - (basePlan?.price || 0);
  // const originalChargeToday = holosMonthlyCharge + smartFitPrice;

  const getConfirmationText = () => {
    return `la membresía ${selectedPlan?.name},  será efectuado inmediatamente y podré empezar a reservar con mi nuevo plan`;
  };

  const showCardVerificationNotice = selectedPaymentOption === 'nextCycle' && selectedMethod === 'new';
  const promoDiscount = appliedPromo ? (holosMonthlyCharge * appliedPromo.discountPercent) : 0;
  const finalTotal = Math.max(0, holosMonthlyCharge - promoDiscount) + smartFitPrice;

  const baseTermsConfirmed = selectedPlan
    ? confirmedTerms.terms && confirmedTerms.price && confirmedTerms.autoRenew && confirmedTerms.cancel
    : confirmedTerms.terms && confirmedTerms.price && confirmedTerms.cancel;
  const smartFitTermsConfirmed = !isSmartFitSelected || confirmedTerms.smartFitTerms;
  const termsConfirmed = baseTermsConfirmed && smartFitTermsConfirmed;

  return (
    <div className="p-6 w-full mx-auto flex-1 overflow-y-auto">
      <div className="font-medium text-4xl font-juana mb-3 border-b border-gray-400">
        Confirma tu {selectedPlan ? 'plan' : 'compra'}
      </div>

      {loading ? <Loader /> : <div className="mb-5">
        <div className="text-lg font-bold">Detalles de pago</div>

        <PaymentSummaryCard
         selectedPaymentOption={selectedPaymentOption}
          className="mt-5 bg-holos-light border-1 border-gray-400 rounded-2xl p-4 text-sm text-gray-800"
          planName={selectedPlan?.name}
          holosMonthlyCharge={holosMonthlyCharge}
          hasPromo={Boolean(appliedPromo)}
          promoDiscount={promoDiscount}
          cycleDuration={selectedPlan?.cycleDuration ?? 1}
          // originalChargeToday={originalChargeToday}
          chargeToday={finalTotal}
          // isSmartFitSelected={isSmartFitSelected}
          // smartFitDuration={smartFitDuration}
          // smartFitPrice={smartFitPrice}
        />

        {paymentMethods.length > 0 && <div className="text-md font-light my-3">Utilizar un método de pago previo:</div>}

        {paymentMethods.length > 0 && (
          <select
            disabled={loading}
            className="border border-gray-300 rounded-full px-3 py-3 bg-gray-100 text-sm text-black w-full cursor-pointer"
            value={selectedMethod}
            onChange={e => setSelectedMethod(e.target.value)}>
            <option value="new">Uso tarjeta nueva</option>
            {paymentMethods.map(pm => (
              <option key={pm.id} value={pm.id} className="capitalize">
                {pm?.issuer?.name.toUpperCase()}  •••• {pm.last_four_digits}
              </option>
            ))}
          </select>
        )}

        {selectedMethod === 'new' && (
          <div>
            <div className="text-md font-light my-3">Utilizar un nuevo método de pago</div>
            <CardFields
              transactionAmount={finalTotal}
              onReady={onBrickReady}
              onBrickSubmit={onBrickSubmit ?? (async () => {})}
              submitDisabled={!termsConfirmed}
              ref={cardFieldsRef}
            />
            {/* <div className="flex items-center my-3">
              <input
                type="checkbox"
                id="save"
                checked={saveNewCard}
                onChange={() => setSaveNewCard(!saveNewCard)}
                className="mr-4 h-6 w-6 text-black accent-black flex-shrink-0 appearance-none border border-gray-300 rounded-full checked:bg-black checked:border-black relative checked:after:content-['✓'] checked:after:text-white checked:after:text-xs checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center"
              />
              <label htmlFor="save" className="cursor-pointer">
                Guardar información para futuros pagos.
              </label>
            </div> */}
          </div>
        )}
        {error && <div className="bg-red-100 text-red-600 font-medium mt-3 border border-red-400">{error}</div>}
      </div>}

      <div className="flex items-center mb-3 border-t border-gray-400 pt-4">
        <input
          type="checkbox"
          id="terms"
          checked={confirmedTerms.terms}
          onChange={() => setConfirmedTerms({ ...confirmedTerms, terms: !confirmedTerms.terms })}
          className="mr-4 h-6 w-6 text-black accent-black flex-shrink-0 appearance-none border border-gray-300 rounded-full checked:bg-black checked:border-black relative checked:after:content-['✓'] checked:after:text-white checked:after:text-xs checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center"
        />
        <label htmlFor="terms" className="cursor-pointer">
          Acepto los <a href="/terminos" className="underline font-medium" target="_blank">Términos y condiciones</a>
        </label>
      </div>
      <div className="flex mb-3">
        <input
          type="checkbox"
          id="price"
          checked={confirmedTerms.price}
          onChange={() => setConfirmedTerms({ ...confirmedTerms, price: !confirmedTerms.price })}
          className="mr-4 h-6 w-6 text-black accent-black flex-shrink-0 appearance-none border border-gray-300 rounded-full checked:bg-black checked:border-black relative checked:after:content-['✓'] checked:after:text-white checked:after:text-xs checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center"
        />
        <label htmlFor="price" className="cursor-pointer">
          Acepto que el cargo de S/ {finalTotal.toFixed(2)} por {getConfirmationText()}.
          {showCardVerificationNotice && ' Para validar tu tarjeta, se realizará un cobro pequeño que será reembolsado inmediatamente.'}
        </label>
      </div>
      <div className="flex mb-3">
        <input
          type="checkbox"
          id="cancel"
          checked={confirmedTerms.cancel}
          onChange={() => setConfirmedTerms({ ...confirmedTerms, cancel: !confirmedTerms.cancel })}
          className="mr-4 h-6 w-6 text-black accent-black flex-shrink-0 appearance-none border border-gray-300 rounded-full checked:bg-black checked:border-black relative checked:after:content-['✓'] checked:after:text-white checked:after:text-xs checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center"
        />
        <label htmlFor="cancel" className="cursor-pointer">
          Puedes cancelar tu nuevo plan 24 horas antes de la fecha de renovación automática.
        </label>
      </div>
      {selectedPlan && <div className="flex mb-3">
        <input
          type="checkbox"
          id="auto-renew"
          checked={confirmedTerms.autoRenew}
          onChange={() => setConfirmedTerms({ ...confirmedTerms, autoRenew: !confirmedTerms.autoRenew })}
          className="mr-4 h-6 w-6 text-black accent-black flex-shrink-0 appearance-none border border-gray-300 rounded-full checked:bg-black checked:border-black relative checked:after:content-['✓'] checked:after:text-white checked:after:text-xs checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center"
        />
        <label htmlFor="auto-renew" className="cursor-pointer">
          Acepto que Holos realizará un débito automático mensual, el cual podrá efectuarse en soles (PEN).
        </label>
      </div>}
      {isSmartFitSelected && <div className="flex mb-3">
        <input
          type="checkbox"
          id="smartfit-terms"
          checked={confirmedTerms.smartFitTerms}
          onChange={() => setConfirmedTerms({ ...confirmedTerms, smartFitTerms: !confirmedTerms.smartFitTerms })}
          className="mr-4 h-6 w-6 text-black accent-black flex-shrink-0 appearance-none border border-gray-300 rounded-full checked:bg-black checked:border-black relative checked:after:content-['✓'] checked:after:text-white checked:after:text-xs checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center"
        />
        <label htmlFor="smartfit-terms" className="cursor-pointer">
          Acepto los <a href={SMART_FIT_TERMS_URL} className="underline font-medium" target="_blank" rel="noopener noreferrer">Términos y condiciones de Smart Fit</a> para el add-on de {smartFitDuration}.
        </label>
      </div>}
    </div>
  );
});